import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMarkNotificationRead, useNotifications } from "../../api/notifications";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterBar } from "../../components/ui/FilterBar";
import { Page } from "../../components/ui/Page";
import { Pagination } from "../../components/ui/Pagination";
import { Spinner } from "../../components/ui/Spinner";
import { Table } from "../../components/ui/Table";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { usePaginationParams } from "../../hooks/usePaginationParams";
import { useSortParams } from "../../hooks/useSortParams";
import { useTranslation } from "../../hooks/useTranslation";
import type { NotificationResponse } from "../../types/notification";
import { formatUserName } from "../../types/user";

const PAGE_SIZE = 20;
type ReadFilter = "all" | "unread";

export default function NotificationsListPage() {
  const { t, language } = useTranslation();
  useLayoutTitle(t("notifications.title"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (searchParams.get("read") ?? "all") as ReadFilter;
  const search = searchParams.get("search")?.trim() || undefined;
  const { sortDir, setSort } = useSortParams<"createdAt">("createdAt");

  const { data, isLoading } = useNotifications({
    search,
    sortDir,
    read: filter === "unread" ? "unread" : undefined,
  });
  const markRead = useMarkNotificationRead();

  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  useEffect(() => {
    data && setNotifications(data);
  }, [data]);

  const { page, totalPages, setPage } = usePaginationParams(PAGE_SIZE, notifications.length);
  const paged = notifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filters: { value: ReadFilter; label: string }[] = [
    { value: "all", label: t("notifications.filterAll") },
    { value: "unread", label: t("notifications.filterUnread") },
  ];

  function handleFilterChange(value: ReadFilter) {
    setSearchParams((prev) => {
      if (value === "all") prev.delete("read");
      else prev.set("read", value);
      prev.set("page", "1");
      return prev;
    });
  }

  function handleClick(n: NotificationResponse) {
    if (!n.readAt) markRead.mutate(n.id);
    navigate(n.entityType === "development_request" ? `/requests/${n.entityId}` : `/inventory/${n.entityId}`);
  }

  return (
    <Page>
      <Page.TitleRow>
        <Page.FilterRow>
          <FilterBar value={filter} options={filters} onChange={handleFilterChange} />
          {isLoading && <Spinner />}
        </Page.FilterRow>
      </Page.TitleRow>

      {paged.length === 0 ? (
        <EmptyState>{t("notifications.noNotifications")}</EmptyState>
      ) : (
        <>
          <Table>
            <Table.Head>
              <Table.HeaderCell>{t("notifications.title")}</Table.HeaderCell>
              <Table.HeaderCell>{t("requests.title")}</Table.HeaderCell>
              <Table.HeaderCell sortDir={sortDir} onSort={() => setSort("createdAt")}>
                {t("requests.createdAt")}
              </Table.HeaderCell>
            </Table.Head>
            <Table.Body>
              {paged.map((n) => (
                <Table.Row key={n.id} onClick={() => handleClick(n)}>
                  <Table.Cell variant={n.readAt ? "muted" : "strong"}>
                    <strong>{formatUserName(n.actor)}</strong> {t(`notifications.actions.${n.action}`)}
                  </Table.Cell>
                  <Table.Cell variant="muted">{n.entityName}</Table.Cell>
                  <Table.Cell variant="muted">{new Date(n.createdAt).toLocaleDateString(language)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </Page>
  );
}
